import React from "react";
import { Form, Formik } from "formik";
import closeIcon from "../../assets/icons/close-icon.svg";
import FilterTextField from "./filter-input-fields/filter-text-field";
import { FilterDropDown } from "./filter-input-fields/filter-dropdown";
import { FilterMultiDropDown } from "./filter-input-fields/filter-multi-dropdown";
import { FilterToggleSwitch } from "./filter-input-fields/filter-switch";
import { FilterFromToDateField } from "./filter-input-fields/filter-from-to-field";
import { uiTypes } from "../../utils/types";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FormDropDownDynamic,
  FormMultiDropDownDynamic,
} from "../form/dynamic-dropdown";
import { FormDropDown, FormMultiSelectDropDown } from "../form/dropdown";
import queryString from "query-string";
import { FormToggleSwitch } from "../form/checkbox";
import { X, RotateCcw, Filter, ListFilter, Check } from "lucide-react";
import { formatUnderscoreToSpace } from "../../utils/text-format";

const FilterDrawer = ({ open, onClose = () => {}, columns, references }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const headings = columns
    .filter((col) => col.ui?.list == null || col.ui.list === true)
    .map((col) => col.name);

  const parsed = queryString.parse(location.search, { arrayFormat: "bracket" });

  // Normalize nested params like filter[name]
  const nestedParsed = {};
  for (const [key, value] of Object.entries(parsed)) {
    const match = key.match(/^(\w+)\[(\w+)\]$/);
    if (match) {
      const [, mainKey, subKey] = match;
      if (!nestedParsed[mainKey]) nestedParsed[mainKey] = {};
      nestedParsed[mainKey][subKey] = value;
    } else {
      nestedParsed[key] = value;
    }
  }

  const initialValues = columns.reduce((data, column) => {
    if (column.ui?.list == null || column.ui.list === true) {
      const name = column.name;
      data[name] = nestedParsed[name] ?? "";
    }
    return data;
  }, {});

  const handleSubmit = (values) => {
    const cleanedValues = Object.fromEntries(
      Object.entries(values).filter(([_, value]) => {
        if (value === false) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "object" && value !== null) {
          return Object.values(value).some(
            (v) => v !== "" && v !== null && v !== false,
          );
        }
        return value !== "" && value !== null && value !== undefined;
      }),
    );

    const flattened = {};
    for (const [key, value] of Object.entries(cleanedValues)) {
      if (
        typeof value === "object" &&
        !Array.isArray(value) &&
        value !== null
      ) {
        for (const [subKey, subVal] of Object.entries(value)) {
          if (subVal !== "" && subVal !== false)
            flattened[`${key}[${subKey}]`] = subVal;
        }
      } else {
        flattened[key] = value;
      }
    }

    const searchParams = queryString.stringify(flattened, {
      arrayFormat: "bracket",
    });
    navigate(`${location.pathname}?${searchParams}`);
    onClose();
  };

  const clearFilter = (resetForm) => {
    navigate(`${location.pathname}`);
    resetForm();
    onClose();
  };

  const enumToOptions = (enumValues = []) =>
    enumValues.map((val) => ({
      label: val.charAt(0).toUpperCase() + val.slice(1),
      value: val,
    }));

  return (
    <>
      {/* --- Backdrop --- */}
      {open && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[60] transition-opacity"
          onClick={onClose}
        />
      )}

      {/* --- Drawer Container --- */}
      <div
        className={`
          fixed top-0 right-0 h-full w-full max-w-[26rem] bg-white shadow-2xl z-[70] 
          transform transition-transform duration-300 ease-out flex flex-col
          ${open ? "translate-x-0" : "translate-x-full"}
        `}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, resetForm }) => (
            <Form className="flex flex-col h-full">
              {/* --- Header --- */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-indigo-50 rounded-lg text-primary">
                    <ListFilter size={18} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Filters</h3>
                </div>
                <button
                  type="button"
                  className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
                  onClick={onClose}
                >
                  <X size={20} />
                </button>
              </div>

              {/* --- Body (Dynamic Fields) --- */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                <div className="space-y-5">
                  {headings.map((heading) => {
                    const column = columns.find((col) => col.name === heading);
                    const label = formatUnderscoreToSpace(heading);

                    // Text Field
                    if (column?.ui?.type === uiTypes?.TEXT) {
                      return (
                        <FilterTextField
                          key={heading}
                          name={heading}
                          label={label}
                          placeholder={`Search ${label}...`}
                        />
                      );
                    }

                    // Switch
                    if (column?.ui?.type === uiTypes?.SWITCH) {
                      return (
                        <div
                          key={heading}
                          className="bg-slate-50 p-3 rounded-xl border border-slate-100"
                        >
                          <FormToggleSwitch
                            name={heading}
                            label={label}
                            value={values?.[heading]}
                          />
                        </div>
                      );
                    }

                    // Dropdowns (Dynamic or Static)
                    if (
                      column?.ui?.type === uiTypes?.DROPDOWN ||
                      column?.ui?.type === uiTypes?.DROPDOWN_MULTISELECT
                    ) {
                      const isMulti =
                        column?.ui?.type === uiTypes?.DROPDOWN_MULTISELECT;
                      const Component = column?.enumValues
                        ? isMulti
                          ? FormMultiSelectDropDown
                          : FormDropDown
                        : isMulti
                          ? FormMultiDropDownDynamic
                          : FormDropDownDynamic;

                      const props = {
                        key: heading,
                        name: heading,
                        label: label,
                        placeholder: `Select ${label}`,
                      };

                      if (column?.enumValues) {
                        props.options = enumToOptions(column.enumValues);
                        props.value = isMulti
                          ? props.options.filter((opt) =>
                              values?.[heading]?.includes(opt.value),
                            )
                          : props.options.find(
                              (opt) => opt.value === values?.[heading],
                            );
                      } else {
                        props.reference = references.find(
                          (ref) => ref.foreignKey === heading,
                        );
                      }

                      return <Component {...props} />;
                    }

                    // Date Range
                    if (
                      column?.ui?.type === uiTypes?.DATE ||
                      column?.ui?.type === uiTypes?.DATE_TIME
                    ) {
                      return (
                        <FilterFromToDateField
                          key={heading}
                          name={heading}
                          label={label}
                          fromValue={`${values?.[heading]?.from ?? ""}`}
                          toValue={`${values?.[heading]?.to ?? ""}`}
                          type={
                            column?.ui?.type === uiTypes?.DATE_TIME
                              ? "datetime-local"
                              : "date"
                          }
                        />
                      );
                    }

                    return null;
                  })}
                </div>
              </div>

              {/* --- Footer Actions --- */}
              <div className="p-5 border-t border-slate-100 bg-slate-50/50 flex gap-3">
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-xl font-bold text-sm shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all active:scale-[0.98]"
                >
                  <Check size={18} /> Apply Results
                </button>

                <button
                  onClick={() => clearFilter(resetForm)}
                  type="button"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-100 transition-all"
                >
                  <RotateCcw size={16} /> Reset Filters
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};
export default FilterDrawer;
